import groq from "groq";
import {
  buildFullPathProjection,
  staticFullPathProjection,
} from "./utils/build-full-path-projection";
import { officeRouteSlugFromOffice } from "./utils/office-route-slug";

/**
 * Stable runtime shape for SB home-page query rows.
 *
 * Sanity typegen currently infers `ActiveSBHomePagesQueryResult` as `never[]`
 * for this query pattern, so consumers should use this explicit source type.
 */
type ActiveSBHomePageItem = {
  officeID: number | null;
  officeName: string | null;
  slug: string | null;
  city: string | null;
  state: string | null;
  country: "CA" | "US" | null;
  type: "SB";
  pageUrl: string | null;
  noIndex?: boolean | null;
  lastModified?: string | null;
};

/**
 * Query: ActiveSBHomePagesQuery
 * Returns a list of all enabled Site Builder office home pages.
 * A Site Builder homepage is a routeNode where:
 * - isEnabled is true
 * - Has a page reference
 * - Has an office in context
 * - The node's slug matches the office ID as a route slug (identifies it as
 *   the homepage). Decimal satellite IDs (116.2) use hyphenated slugs (116-2).
 *
 * Sorted ascending by office ID.
 * Includes `noIndex` and `lastModified` fields for sitemap filtering/output.
 * Filters by country: $country should be "US" or "CA".
 *
 * @note Tagged with `@sanity-typegen-ignore` because `buildFullPathProjection()`
 * is dynamic. Sanity TypeGen cannot evaluate the function body and would error
 * out. A static sibling, `typegenSafeActiveSBHomePagesQuery`, exists below
 * solely to give TypeGen a parseable shape; consumers continue to use this
 * runtime query.
 */
// @sanity-typegen-ignore
const ActiveSBHomePagesQuery = groq`
*[
  _type == "routeNode" &&
  isEnabled == true &&
  defined(page._ref) &&
  count(context[_type == "officeRef"]) > 0
] {
  "office": context[_type == "officeRef"][0]->,
  "nodeSlug": slug.current,
  ${buildFullPathProjection()},
  "pageNoIndex": page->seoSettings.noIndex,
  hideFromSitemap,
  "lastModified": select(
    defined(page->_updatedAt) && page->_updatedAt >= _updatedAt => page->_updatedAt,
    true => _updatedAt
  )
}[
  office != null &&
  office.enabled == true &&
  defined(office.urlSlug) &&
  office.address.country == $country &&
  nodeSlug == ${officeRouteSlugFromOffice}
] | order(office.officeID asc) {
  "officeID": office.officeID,
  "officeName": office.locationName,
  "slug": office.urlSlug,
  "city": office.address.city,
  "state": office.address.stateProvince,
  "country": office.address.country,
  "type": "SB",
  "noIndex": pageNoIndex,
  hideFromSitemap,
  "lastModified": lastModified,
  "pageUrl": fullPath
}`;

/**
 * Static sibling of {@link ActiveSBHomePagesQuery} that exists ONLY for Sanity
 * TypeGen to extract a result type. The runtime query above uses
 * `buildFullPathProjection()`, which TypeGen cannot evaluate. Keep this
 * structurally in sync with the dynamic query above (only the `fullPath`
 * projection differs — static expansion vs. dynamic builder).
 *
 * Pattern mirrors `routeMatchQuery` in
 * `packages/site-builder/src/queries/route-data/build-route-data-query.ts`.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const typegenSafeActiveSBHomePagesQuery = groq`
*[
  _type == "routeNode" &&
  isEnabled == true &&
  defined(page._ref) &&
  count(context[_type == "officeRef"]) > 0
] {
  "office": context[_type == "officeRef"][0]->,
  "nodeSlug": slug.current,
  ${staticFullPathProjection},
  "pageNoIndex": page->seoSettings.noIndex,
  hideFromSitemap,
  "lastModified": select(
    defined(page->_updatedAt) && page->_updatedAt >= _updatedAt => page->_updatedAt,
    true => _updatedAt
  )
}[
  office != null &&
  office.enabled == true &&
  defined(office.urlSlug) &&
  office.address.country == $country &&
  nodeSlug == ${officeRouteSlugFromOffice}
] | order(office.officeID asc) {
  "officeID": office.officeID,
  "officeName": office.locationName,
  "slug": office.urlSlug,
  "city": office.address.city,
  "state": office.address.stateProvince,
  "country": office.address.country,
  "type": "SB",
  "noIndex": pageNoIndex,
  hideFromSitemap,
  "lastModified": lastModified,
  "pageUrl": fullPath
}`;

export type { ActiveSBHomePageItem };

export { ActiveSBHomePagesQuery };
